export interface StreamingEvent {
  type: string
  data?: any
  params?: {
    eventType?: string
    contentBlock?: {
      type?: string
      content?: {
        text?: string
      }
    }
    [key: string]: any
  }
  [key: string]: any
}

interface AssistantResponseBlock {
  startTime: string
  deltas: string[]
  accumulatedText: string
  completedText?: string
  isComplete: boolean
}

export function normalizeStreamingResponse(events: StreamingEvent[]): string {
  const assistantBlocks: AssistantResponseBlock[] = []
  let currentBlock: AssistantResponseBlock | null = null
  const stageLines: string[] = []

  for (const event of events) {
    const eventType =
      event.params?.eventType ||
      event.type ||
      event.data?.params?.eventType ||
      event.data?.eventType ||
      ''

    if (eventType === 'stream_start') {
      stageLines.push('Initializing...')
      continue
    }

    if (eventType === 'agent_handoff') {
      stageLines.push('Routing to specialist...')
      continue
    }

    if (eventType === 'assistant-response.start') {
      stageLines.push('Preparing response...')
      if (currentBlock) {
        assistantBlocks.push(currentBlock)
      }

      currentBlock = {
        startTime:
          event.params?.timestamp ||
          event.timestamp ||
          event.data?.params?.timestamp ||
          '',
        deltas: [],
        accumulatedText: '',
        isComplete: false,
      }
      continue
    }

    if (eventType === 'assistant-response.delta' && currentBlock) {
      const text = event.params?.contentBlock?.content?.text || ''
      if (text) {
        if (text.startsWith(currentBlock.accumulatedText)) {
          const newText = text.slice(currentBlock.accumulatedText.length)
          if (newText) {
            currentBlock.deltas.push(newText)
            currentBlock.accumulatedText = text
          }
        } else if (!currentBlock.accumulatedText.includes(text)) {
          currentBlock.deltas.push(text)
          currentBlock.accumulatedText += text
        }
      }
      continue
    }

    if (eventType === 'assistant-response.completed') {
      const completedText = event.params?.contentBlock?.content?.text || ''
      if (currentBlock) {
        if (completedText) {
          currentBlock.completedText = completedText
        }
        currentBlock.isComplete = true
        assistantBlocks.push(currentBlock)
        currentBlock = null
      } else if (completedText) {
        assistantBlocks.push({
          startTime:
            event.params?.timestamp ||
            event.timestamp ||
            event.data?.params?.timestamp ||
            '',
          deltas: [],
          accumulatedText: completedText,
          completedText: completedText,
          isComplete: true,
        })
      }
      continue
    }
  }

  if (currentBlock) {
    assistantBlocks.push(currentBlock)
  }

  if (assistantBlocks.length === 0) {
    return ''
  }

  const normalizedBlocks: string[] = []

  for (const block of assistantBlocks) {
    let blockText = ''
    if (block.completedText) {
      blockText = block.completedText
    } else if (block.deltas.length > 0) {
      blockText = block.deltas.join('')
    }

    const trimmed = blockText.trim()
    if (!trimmed) continue

    const cleaned = deduplicateText(trimmed).trim()
    if (cleaned) {
      normalizedBlocks.push(cleaned)
    }
  }

  if (normalizedBlocks.length === 0) {
    return ''
  }

  const finalBlocks: string[] = []

  normalizedBlocks.forEach(current => {
    const curNorm = current.replace(/\s+/g, ' ').trim().toLowerCase()
    if (!curNorm) return

    let isRedundant = false
    for (let i = 0; i < finalBlocks.length; i++) {
      const existing = finalBlocks[i]
      const existNorm = existing.replace(/\s+/g, ' ').trim().toLowerCase()

      const shorter = curNorm.length < existNorm.length ? curNorm : existNorm
      const longer = curNorm.length < existNorm.length ? existNorm : curNorm

      const isContained = longer.includes(shorter) && shorter.length > 0

      if (isContained) {
        if (longer === curNorm) {
          finalBlocks[i] = current
        }
        isRedundant = true
        break
      }
    }

    if (!isRedundant) {
      finalBlocks.push(current)
    }
  })

  const finalText = finalBlocks.join('\n\n').trim()

  if (!finalText) {
    return ''
  }

  const uniqueStages = stageLines
    .map(s => s.trim())
    .filter(Boolean)
    .filter((s, index, arr) => arr.indexOf(s) === index)

  if (!uniqueStages.length) {
    return finalText
  }

  const stagesMarkdown = uniqueStages.map(s => `✓ ${s}`).join('\n')
  return `${stagesMarkdown}\n\n${finalText}`.trim()
}

function deduplicateText(text: string): string {
  const paragraphs = text
    .split(/\n\s*\n/)
    .map(p => p.trim())
    .filter(p => p.length > 0)

  if (paragraphs.length === 0) {
    return text
  }

  const seen = new Set<string>()
  const uniqueParagraphs: string[] = []

  for (const paragraph of paragraphs) {
    const normalized = paragraph.replace(/\s+/g, ' ').toLowerCase().trim()

    let isDuplicate = false
    for (const seenPara of seen) {
      if (
        normalized === seenPara ||
        (normalized.length > 20 && seenPara.includes(normalized)) ||
        (seenPara.length > 20 && normalized.includes(seenPara))
      ) {
        isDuplicate = true
        break
      }
    }

    if (!isDuplicate) {
      seen.add(normalized)
      uniqueParagraphs.push(paragraph)
    }
  }

  if (uniqueParagraphs.length < paragraphs.length) {
    return uniqueParagraphs.join('\n\n')
  }

  const sentences = text
    .split(/[.!?]\s+/)
    .map(s => s.trim())
    .filter(s => s.length > 0)
  if (sentences.length <= 1) {
    return text
  }

  const seenSentences = new Set<string>()
  const uniqueSentences: string[] = []

  for (const sentence of sentences) {
    const normalized = sentence.replace(/\s+/g, ' ').toLowerCase().trim()

    if (!seenSentences.has(normalized)) {
      seenSentences.add(normalized)
      uniqueSentences.push(sentence)
    }
  }

  return uniqueSentences
    .map((s, i) => {
      if (!/[.!?]$/.test(s)) {
        return s + '.'
      }
      return s
    })
    .join(' ')
}

export function processStreamingEvents(events: StreamingEvent[]): string {
  return normalizeStreamingResponse(events)
}
